import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { adminApi, AdminUser, UserInvitation, InviteUserRequest } from '../services/adminApi';
import { AdminShell } from '../components/admin/AdminShell';
import { theme, neutral, blueGrey } from '../design-system/theme';
import '../design-system/styles.css';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ActionButtons,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  Form,
  FormGroup,
  Label,
  Input,
  Select,
  Textarea,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner,
  TabButtonGroup,
  TabButton,
  FilterButtonGroup,
  FilterButton,
  RoleBadge,
} from '../styles/adminShared';

type ViewMode = 'users' | 'invitations';
type InvitationFilter = 'ALL' | 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';

// Page-specific styled components (only those not in shared)
const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${theme.textPrimary};
`;

const UserEmail = styled.div`
  color: ${theme.textSecondary};
  font-size: 12px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: ${blueGrey[1]};
  border-radius: 8px;
`;

const StatLabel = styled.div`
  color: ${theme.textSecondary};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${theme.textPrimary};
`;

const EmptyStateMessage = styled.p`
  color: ${theme.textSecondary};
  font-size: 14px;
`;

const RoleChangeSection = styled.div`
  border-top: 1px solid ${neutral[3]};
  padding-top: 24px;
  margin-bottom: 24px;
`;

const TabSection = styled.div`
  margin-bottom: 24px;
`;

const FilterSection = styled.div`
  margin-bottom: 20px;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

// Format role for display
const formatRole = (role: string): string => {
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * User Management page for managing tenant users and invitations.
 */
export const UserManagement: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [invitationFilter, setInvitationFilter] = useState<InvitationFilter>('PENDING');
  
  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await adminApi.listUsers();
      setUsers(response.users);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const status = invitationFilter === 'ALL' ? undefined : invitationFilter;
      const response = await adminApi.listInvitations(status);
      setInvitations(response.invitations);
    } catch (err) {
      console.error('Failed to fetch invitations', err);
    }
  }, [invitationFilter]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    Promise.all([fetchUsers(), fetchInvitations()])
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load data'))
      .finally(() => setLoading(false));
  }, [fetchUsers, fetchInvitations]);

  const handleInviteUser = async (request: InviteUserRequest) => {
    setActionLoading(true);
    setError(null);
    
    try {
      await adminApi.inviteUser(request);
      setShowInviteModal(false);
      setSuccessMessage(`Invitation sent to ${request.email}`);
      fetchInvitations();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to send invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    setActionLoading(true);
    try {
      await adminApi.resendInvitation(invitationId);
      setSuccessMessage('Invitation resent successfully');
      fetchInvitations();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) return;
    
    setActionLoading(true);
    try {
      await adminApi.revokeInvitation(invitationId);
      setSuccessMessage('Invitation revoked');
      fetchInvitations();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    setActionLoading(true);
    try {
      await adminApi.updateUserRole(userId, newRole);
      setSelectedUser(null);
      setSuccessMessage('User role updated');
      fetchUsers();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will no longer be able to log in.')) return;
    
    setActionLoading(true);
    try {
      await adminApi.deactivateUser(userId);
      setSelectedUser(null);
      setSuccessMessage('User deactivated');
      fetchUsers();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
    } finally {
      setActionLoading(false);
    }
  };



  return (
    <AdminShell>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>User Management</PageTitle>
            <PageSubtitle>Manage team members and invitations</PageSubtitle>
          </div>
          <PrimaryButton onClick={() => setShowInviteModal(true)}>
            + Invite User
          </PrimaryButton>
        </PageHeader>

        {/* Messages */}
        {error && (
          <AlertMessage $variant="error">
            {error}
            <button 
              onClick={() => setError(null)} 
              style={{ marginLeft: 8, cursor: 'pointer', background: 'none', border: 'none', fontSize: 'inherit' }}
            >
              ×
            </button>
          </AlertMessage>
        )}
        {successMessage && (
          <AlertMessage $variant="success">
            {successMessage}
          </AlertMessage>
        )}

        {/* View Toggle */}
        <TabSection>
          <TabButtonGroup>
            <TabButton
              $active={viewMode === 'users'}
              onClick={() => setViewMode('users')}
            >
              👥 Users ({users.length})
            </TabButton>
            <TabButton
              $active={viewMode === 'invitations'}
              onClick={() => setViewMode('invitations')}
            >
              📧 Invitations ({invitations.length})
            </TabButton>
          </TabButtonGroup>
        </TabSection>

        {/* Content */}
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : viewMode === 'users' ? (
          /* Users Table */
          <>
            {users.length === 0 ? (
              <EmptyStateContainer>
                <EmptyStateTitle>No users found</EmptyStateTitle>
                <EmptyStateText>Invite someone to get started!</EmptyStateText>
              </EmptyStateContainer>
            ) : (
              <TableContainer>
                <Table>
                  <thead>
                    <Tr>
                      <Th>User</Th>
                      <Th>Role</Th>
                      <Th>Status</Th>
                      <Th>Joined</Th>
                      <Th>Last Login</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <Tr key={user.userId}>
                        <Td>
                          <UserInfoContainer>
                            <UserName>{user.name || 'Unnamed'}</UserName>
                            <UserEmail>{user.email}</UserEmail>
                          </UserInfoContainer>
                        </Td>
                        <Td>
                          <RoleBadge $role={user.role}>
                            {formatRole(user.role)}
                          </RoleBadge>
                        </Td>
                        <Td>
                          <StatusBadge $status={user.active === false ? 'INACTIVE' : user.status || 'ACTIVE'}>
                            {user.active === false ? 'Inactive' : user.status || 'Active'}
                          </StatusBadge>
                        </Td>
                        <Td>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                        </Td>
                        <Td>
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                        </Td>
                        <Td>
                          <IconButton $variant="view" onClick={() => setSelectedUser(user)}>
                            ⚙️ Manage
                          </IconButton>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          /* Invitations */
          <>
            {/* Filter */}
            <FilterSection>
              <FilterButtonGroup>
                {['PENDING', 'ACCEPTED', 'EXPIRED', 'REVOKED', 'ALL'].map(status => (
                  <FilterButton
                    key={status}
                    $active={invitationFilter === status}
                    onClick={() => setInvitationFilter(status as InvitationFilter)}
                  >
                    {status.charAt(0) + status.slice(1).toLowerCase()}
                  </FilterButton>
                ))}
              </FilterButtonGroup>
            </FilterSection>

            {/* Invitations Table */}
            <>
              {invitations.length === 0 ? (
                <EmptyStateContainer>
                  <EmptyStateTitle>No invitations found</EmptyStateTitle>
                  <EmptyStateText>Create one to get started.</EmptyStateText>
                </EmptyStateContainer>
              ) : (
                <TableContainer>
                  <Table>
                    <thead>
                      <Tr>
                        <Th>Invitee</Th>
                        <Th>Role</Th>
                        <Th>Status</Th>
                        <Th>Invited By</Th>
                        <Th>Expires</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </thead>
                    <tbody>
                      {invitations.map((inv) => (
                        <Tr key={inv.invitationId}>
                          <Td>
                            <UserInfoContainer>
                              <UserName>{inv.name || 'Not specified'}</UserName>
                              <UserEmail>{inv.email}</UserEmail>
                            </UserInfoContainer>
                          </Td>
                          <Td>
                            <RoleBadge $role={inv.role}>
                              {formatRole(inv.role)}
                            </RoleBadge>
                          </Td>
                          <Td>
                            <StatusBadge $status={inv.status}>
                              {inv.status.charAt(0) + inv.status.slice(1).toLowerCase()}
                            </StatusBadge>
                          </Td>
                          <Td>
                            <UserInfoContainer>
                              <UserName>{inv.invitedByName || 'Unknown'}</UserName>
                              <UserEmail>
                                {inv.invitedAt ? new Date(inv.invitedAt).toLocaleDateString() : '-'}
                              </UserEmail>
                            </UserInfoContainer>
                          </Td>
                          <Td>
                            {inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : '-'}
                          </Td>
                          <Td>
                            {inv.status === 'PENDING' && (
                              <ActionButtons>
                                <IconButton
                                  $variant="success"
                                  onClick={() => {
                                    if (inv.invitationLink) {
                                      navigator.clipboard.writeText(inv.invitationLink);
                                      setSuccessMessage('Invitation link copied to clipboard!');
                                      setTimeout(() => setSuccessMessage(null), 3000);
                                    }
                                  }}
                                  title="Copy invitation link to share directly"
                                >
                                  🔗 Copy
                                </IconButton>
                                <IconButton
                                  $variant="view"
                                  onClick={() => handleResendInvitation(inv.invitationId)}
                                  disabled={actionLoading}
                                  title="Resend invitation email"
                                >
                                  📧 Resend
                                </IconButton>
                                <IconButton
                                  $variant="danger"
                                  onClick={() => handleRevokeInvitation(inv.invitationId)}
                                  disabled={actionLoading}
                                  title="Revoke invitation"
                                >
                                  ❌ Revoke
                                </IconButton>
                              </ActionButtons>
                            )}
                            {inv.status === 'ACCEPTED' && (
                              <EmptyStateMessage>
                                Joined {inv.acceptedAt ? new Date(inv.acceptedAt).toLocaleDateString() : ''}
                              </EmptyStateMessage>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>
              )}
            </>
          </>
        )}
      </PageContainer>

      {/* Invite User Modal */}
      <ModalOverlay $show={showInviteModal} onClick={() => setShowInviteModal(false)}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <InviteUserModal
            onClose={() => setShowInviteModal(false)}
            onInvite={handleInviteUser}
            loading={actionLoading}
          />
        </ModalContent>
      </ModalOverlay>

      {/* User Detail Modal */}
      <ModalOverlay $show={!!selectedUser} onClick={() => setSelectedUser(null)}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          {selectedUser && (
            <UserDetailModal
              user={selectedUser}
              onClose={() => setSelectedUser(null)}
              onUpdateRole={handleUpdateRole}
              onDeactivate={handleDeactivateUser}
              loading={actionLoading}
            />
          )}
        </ModalContent>
      </ModalOverlay>
    </AdminShell>
  );
};

// ========================================
// Invite User Modal
// ========================================

interface InviteUserModalProps {
  onClose: () => void;
  onInvite: (request: InviteUserRequest) => void;
  loading: boolean;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ onClose, onInvite, loading }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('CONSENT_MANAGER');
  const [customMessage, setCustomMessage] = useState('');
  const [dataCollectorIds, setDataCollectorIds] = useState<string[]>([]);
  const [dataCollectors, setDataCollectors] = useState<{dataCollectorId: string; name: string}[]>([]);

  // Fetch data collectors for fiduciary scoping
  useEffect(() => {
    if (role === 'DPO' || role === 'CONSENT_MANAGER') {
      adminApi.listDataCollectors(1, 100).then((res: any) => {
        const items = res?.data || res?.items || [];
        setDataCollectors(Array.isArray(items) ? items : []);
      }).catch(() => setDataCollectors([]));
    }
  }, [role]);

  const isScopedRole = role === 'DPO' || role === 'CONSENT_MANAGER';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onInvite({
      email,
      name: name || undefined,
      role,
      customMessage: customMessage || undefined,
      ...(isScopedRole && dataCollectorIds.length > 0 ? { dataCollectorIds } : {}),
    } as any);
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle>Invite Team Member</ModalTitle>
        <CloseButton onClick={onClose}>×</CloseButton>
      </ModalHeader>

      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="name">Name (optional)</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setName(e.target.value)}
              placeholder="John Doe"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="role">Role *</Label>
            <Select
              id="role"
              value={role}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setRole(e.target.value as any)}
              required
            >
              <option value="ADMIN">Admin - Full access to all features</option>
              <option value="DPO">Data Protection Officer - Compliance oversight, scoped to fiduciaries</option>
              <option value="CONSENT_MANAGER">Consent Manager - Manage templates and view records</option>
              <option value="AUDITOR">Auditor - Read-only with full audit log access</option>
              <option value="DATA_CONTROLLER">Data Controller - View and export data</option>
              <option value="VIEWER">Viewer - Read-only access</option>
            </Select>
          </FormGroup>

          {isScopedRole && (
            <FormGroup>
              <Label htmlFor="dataCollectors">Linked Data Fiduciaries *</Label>
              <div style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: 8, maxHeight: 160, overflowY: 'auto', background: '#fff' }}>
                {dataCollectors.length === 0 ? (
                  <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>Loading fiduciaries...</p>
                ) : (
                  dataCollectors.map(dc => (
                    <label key={dc.dataCollectorId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={dataCollectorIds.includes(dc.dataCollectorId)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDataCollectorIds(prev => [...prev, dc.dataCollectorId]);
                          } else {
                            setDataCollectorIds(prev => prev.filter(id => id !== dc.dataCollectorId));
                          }
                        }}
                      />
                      <span style={{ fontSize: 14 }}>{dc.name || dc.dataCollectorId}</span>
                    </label>
                  ))
                )}
              </div>
              {isScopedRole && dataCollectorIds.length === 0 && (
                <p style={{ margin: '4px 0 0', color: '#dc2626', fontSize: 12 }}>Select at least one fiduciary</p>
              )}
            </FormGroup>
          )}

          <FormGroup>
            <Label htmlFor="customMessage">Personal Message (optional)</Label>
            <Textarea
              id="customMessage"
              value={customMessage}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setCustomMessage(e.target.value)}
              placeholder="Add a personal note to the invitation email..."
              rows={3}
            />
          </FormGroup>
        </Form>
      </ModalBody>

      <ModalFooter>
        <SecondaryButton type="button" onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={loading || !email || (isScopedRole && dataCollectorIds.length === 0)} onClick={handleSubmit}>
          Send Invitation
        </PrimaryButton>
      </ModalFooter>
    </>
  );
};

// ========================================
// User Detail Modal
// ========================================

interface UserDetailModalProps {
  user: AdminUser;
  onClose: () => void;
  onUpdateRole: (userId: string, role: string) => void;
  onDeactivate: (userId: string) => void;
  loading: boolean;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  user,
  onClose,
  onUpdateRole,
  onDeactivate,
  loading,
}) => {
  const [newRole, setNewRole] = useState(user.role);

  // Get current user info to prevent self-deactivation
  const currentUserInfo = localStorage.getItem('userInfo');
  const currentUserId = currentUserInfo ? JSON.parse(currentUserInfo).userId : null;
  const isSelf = currentUserId === user.userId;

  return (
    <>
      <ModalHeader>
        <div>
          <ModalTitle>{user.name || 'Unnamed User'}</ModalTitle>
          <EmptyStateMessage style={{ margin: '4px 0 0 0' }}>{user.email}</EmptyStateMessage>
        </div>
        <CloseButton onClick={onClose}>×</CloseButton>
      </ModalHeader>

      <ModalBody>
        <StatsGrid>
          <StatItem>
            <StatLabel>Status</StatLabel>
            <StatValue>{user.active === false ? 'Inactive' : 'Active'}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Current Role</StatLabel>
            <StatValue>{formatRole(user.role)}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Joined</StatLabel>
            <StatValue>
              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
            </StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Last Login</StatLabel>
            <StatValue>
              {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
            </StatValue>
          </StatItem>
        </StatsGrid>

        {/* Role Change */}
        {user.active !== false && (
          <RoleChangeSection>
            <FormGroup>
              <Label htmlFor="newRole">Change Role</Label>
              <Select
                id="newRole"
                value={newRole}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setNewRole(e.target.value as any)}
              >
                <option value="ADMIN">Admin</option>
                <option value="DPO">Data Protection Officer</option>
                <option value="CONSENT_MANAGER">Consent Manager</option>
                <option value="AUDITOR">Auditor</option>
                <option value="DATA_CONTROLLER">Data Controller</option>
                <option value="VIEWER">Viewer</option>
              </Select>
            </FormGroup>

            {newRole !== user.role && (
              <PrimaryButton
                onClick={() => onUpdateRole(user.userId, newRole)}
                disabled={loading}
              >
                Update Role
              </PrimaryButton>
            )}
          </RoleChangeSection>
        )}
      </ModalBody>

      <ModalFooter>
        <div>
          {user.active !== false && !isSelf && (
            <DangerButton
              onClick={() => onDeactivate(user.userId)}
              disabled={loading}
            >
              Deactivate User
            </DangerButton>
          )}
          {isSelf && (
            <EmptyStateMessage style={{ fontSize: 12 }}>
              You cannot deactivate your own account
            </EmptyStateMessage>
          )}
        </div>
        <SecondaryButton onClick={onClose}>
          Close
        </SecondaryButton>
      </ModalFooter>
    </>
  );
};

export default UserManagement;
